import {
  isRouteErrorResponse,
  Links,
  Meta,
  Scripts,
  ScrollRestoration,
  useOutlet,
  useRouteError,
} from 'react-router';
import styles from '@/css/tailwind.css?url';
import { MotionConfig } from '@/components/context/motionConfig';
import { CMSContentProvider } from '@/components/context/cmsContentProvider';
import { ErrorPage } from '@/components/fullPages/errorPage';
import type { Route } from './+types/root';
import { DevErrorPage } from '@/components/fullPages/devErrorPage';
import giftcardCms from '@/api/cms/cms.json';
import { PageContainer } from '@/components/fullPages/pageContainer';
import { env } from '@/utils/env.server';
import { NavHeader } from '@/components/custom/navHeader';

export const links: Route.LinksFunction = () => {
  return [
    {
      rel: 'icon',
      href: '/favicon.svg',
      type: 'image/svg+xml',
    },
    { rel: 'stylesheet', href: styles },
  ];
};

export const loader = () => ({
  APP_SKIP_ANIMATIONS: env.APP_SKIP_ANIMATIONS,
  APP_DISABLE_BUY: env.APP_DISABLE_BUY,
  APP_TERMS_OF_SERVICE_URL: env.APP_TERMS_OF_SERVICE_URL,
  APP_PRIVACY_POLICY_URL: env.APP_PRIVACY_POLICY_URL,
  giftcardCms,
});

export const shouldRevalidate = () => false;

export const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <html lang="nb-NO">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="color-scheme" content="light dark" />
        <Meta />
        <Links />
      </head>
      <body
        className={
          'font-sans bg-background text-foreground relative h-dvh print:h-full flex flex-col'
        }
      >
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
};

const AppInner = ({
  loaderData: { APP_SKIP_ANIMATIONS, giftcardCms },
  matches,
}: Route.ComponentProps) => {
  const outlet = useOutlet();
  const err = useRouteError();

  const key = `${matches[1]?.pathname ?? 'root'}${err ? '_error' : ''}`;

  return (
    <MotionConfig shouldSkipAnimations={APP_SKIP_ANIMATIONS}>
      <CMSContentProvider {...giftcardCms}>
        <PageContainer
          initial
          pageKey={key}
          className={'h-full w-full relative grow shrink flex flex-col'}
        >
          <NavHeader />

          {isRouteErrorResponse(err) ? <ErrorPage /> : outlet}
        </PageContainer>
      </CMSContentProvider>
    </MotionConfig>
  );
};

const App = (props: Route.ComponentProps) => <AppInner {...props} />;

export default App;

export const ErrorBoundary =
  import.meta.env.MODE === 'development' ? DevErrorPage : ErrorPage;
